import { useMemo } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useDataModel } from '@/hooks/useDataModel';
import { DataModelAttribute, PersistedItem } from '@/types/DataModel';

/**
 * Attribute types the legacy `generateCellEditors` did not expose an
 * editor for, even though the underlying attribute may exist on the layout.
 * For all of these we render read-only cells - same as legacy
 * `enableCellEdit = false` in items-controller.js / application-controller.js.
 */
const NON_EDITABLE_TYPES = new Set<string>([
    // Composites have no editor (see items-controller.js filter around line 480).
    'Composite',
    // Structural / read-only-in-grid types (legacy set enableCellEdit=false).
    'AdditionalCategory',
    'TextArea',
    'MultiReference',
    'MultiDimensional',
    'Collection',
    'SingleReference',
    'Group',
    'Password',
]);

/**
 * Renderers that legacy explicitly disabled inline editing for
 * (application-controller.js `generateCellEditors`, e.g. `HTMLDimensional`,
 * `HTMLInput`).
 *
 * `SingleBoolean` is intentionally *not* in this set: the legacy grid
 * disabled `enableCellEdit` for it only because it shipped its own
 * `Grid-SingleBoolean` cell template with an inline checkbox, so users
 * could toggle the value without a separate edit mode. The browse hit
 * list always shows that checkbox and toggles it on one click, which
 * enters edit mode and writes the value through `BooleanComponent`.
 */
const NON_EDITABLE_RENDERERS = new Set<string>([
    'HTMLDimensional',
    'HTMLInput',
]);

interface CellEditableResult {
    /**
     * Whether the cell may enter edit mode on double-click. Combines:
     * - user edit permission on the item (legacy `row.editable`)
     * - attribute state (`attributeStates__` readonly / hidden entries on the item)
     * - category membership (item's category defines which attributes it exposes)
     * - attribute renderer / type (legacy `generateCellEditors` no-editor branches).
     */
    editable: boolean;
    /** True when the cell should be hidden for this item (legacy `isGridAttributeHidden`). */
    hidden: boolean;
    /** True when the attribute is readonly for this item (legacy `isGridAttributeReadOnly`). */
    readonly: boolean;
    /** The attribute is not part of this item's category. */
    categoryMismatch: boolean;
    /**
     * Editable when category membership is ignored. Hit-list cells still
     * open an empty editor in that case (double-click, same as a null
     * value on an attribute the category does own).
     */
    editableIfInCategory: boolean;
}

/**
 * Reads the `attributeStates__` array on a persisted item and returns the
 * set of state flags for a given attribute name. The array uses
 * `"<attributeName>:<state1>,<state2>"` entries, same format
 * `useAttributesStateStore.initializeWithStates` parses.
 */
const getStatesForAttribute = (
    item: PersistedItem | undefined,
    attributeName: string,
): Set<string> => {
    const states = new Set<string>();
    if (!item?.attributeStates__) return states;
    for (const entry of item.attributeStates__) {
        const [name, statesString] = entry.split(':');
        if (name?.trim() !== attributeName) continue;
        statesString
            ?.split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((s) => states.add(s));
    }
    return states;
};

/**
 * React port of `itemCellEditableCondition` (items-controller.js 418-438)
 * combined with `isGridAttributeReadOnly` / `isGridAttributeHidden`
 * (application-controller.js 1086-1124).
 */
const useCellEditableCondition = (
    item: PersistedItem | undefined,
    attribute: DataModelAttribute | undefined,
): CellEditableResult => {
    const { auth } = useAuth();
    const { dataModel } = useDataModel();

    return useMemo<CellEditableResult>(() => {
        if (!item || !attribute) {
            return {
                editable: false,
                hidden: false,
                readonly: false,
                categoryMismatch: false,
                editableIfInCategory: false,
            };
        }

        const states = getStatesForAttribute(item, attribute.name);
        const readonlyByState = states.has('readonly');

        // `readonlyAttributes__` is a legacy server-side hint not modelled
        // on PersistedItem; read it defensively without an unsafe cast.
        const readonlyAttrs = (item as unknown as {
            readonlyAttributes__?: string[];
        }).readonlyAttributes__;
        const readonlyByAttrList = readonlyAttrs?.includes(attribute.name) ?? false;

        // Category membership: if the item has a category, the attribute
        // must be part of that category's attribute list. Legacy
        // `isGridAttributeHidden` and `isGridAttributeReadOnly` both return
        // `!inCategory`, so a category-mismatched cell is *hidden entirely*
        // in the AngularJS grid - not merely read-only. Fold the check into
        // `hidden` so category-only attributes like `string_fixed_5` on the
        // `ValidatedDemoItem` category don't render as an empty cell that
        // refuses to enter edit mode on `SimpleDemoItem`/`BaseDemoItem`
        // rows.
        let categoryMismatch = false;
        if (item.category__ && dataModel?.categoryAttributes) {
            const categoryAttrs = dataModel.categoryAttributes(item.category__);
            categoryMismatch = !categoryAttrs?.some(
                (a) => a.name === attribute.name,
            );
        }

        const hiddenByState = states.has('hidden');
        const hidden = hiddenByState || categoryMismatch;

        const readonlyWithoutCategory =
            readonlyByState ||
            readonlyByAttrList ||
            Boolean(attribute.readonly) ||
            Boolean(attribute.additionalParams?.readonly);

        const readonly = readonlyWithoutCategory || categoryMismatch;

        const rendererName =
            typeof attribute.params?.renderer === 'string'
                ? attribute.params.renderer
                : undefined;

        const typeSupportsInlineEditor =
            !NON_EDITABLE_TYPES.has(attribute.typeName || '') &&
            !(rendererName && NON_EDITABLE_RENDERERS.has(rendererName));

        let hasEditPermission = true;
        if (auth) {
            hasEditPermission = auth.hasItemPermission('edit', item);
        }

        const editable =
            hasEditPermission &&
            typeSupportsInlineEditor &&
            !readonly &&
            !hidden;

        const editableIfInCategory =
            hasEditPermission &&
            typeSupportsInlineEditor &&
            !readonlyWithoutCategory &&
            !hiddenByState;

        return { editable, hidden, readonly, categoryMismatch, editableIfInCategory };
    }, [item, attribute, auth, dataModel]);
};

export { useCellEditableCondition };
export type { CellEditableResult };
